import { OrganizationLogo } from '@features/organizations'
import { Button, FullPageCard } from '@st/theme'
import { PublicOrganization } from '@st/sdk'
import { match, P } from '@st/pattern'

export function ClientMfaGate({
  organization,
  returnType,
  last4,
  showError,
  onLast4Changed,
  onContinue
}: {
  organization: PublicOrganization
  returnType: string
  last4: string
  showError: boolean
  onLast4Changed: (value: string) => void
  onContinue: () => void
}) {
  const inputId = 'client-mfa-last4'
  const errorId = 'client-mfa-last4-error'
  const identifier = match(returnType)
    .with(P.union('1120', '1120s', '1065'), () => 'EIN')
    .otherwise(() => 'SSN')

  return (
    <FullPageCard
      footer={
        <p className="mt-10 text-center text-sm text-gray-500">
          Powered by StanfordTax, Inc.
          <br /> 222 Broadway, New York, 10032
        </p>
      }
    >
      <div className="flex flex-col gap-1">
        {organization.logo && (
          <OrganizationLogo className="mx-auto" src={organization.logo} size={80} />
        )}
        <h3 className="text-center text-xl font-bold leading-9 tracking-tight text-gray-900">
          {organization.name ?? ' '}
        </h3>
      </div>

      <h1 className="mt-8 text-center text-2xl">Verification Required</h1>
      <p className="my-4 text-center text-base text-gray-700">
        For security purposes, please enter the last 4 digits of your {identifier}.
      </p>

      <div className="flex flex-col items-center gap-4">
        <label htmlFor={inputId} className="sr-only">
          Last 4 digits of {identifier}
        </label>
        <input
          id={inputId}
          type="text"
          value={last4}
          onChange={(e) => onLast4Changed(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          autoFocus={true}
          placeholder={`Last 4 digits of ${identifier}`}
          className="w-3/4 rounded-md border-0 py-1.5 text-center text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
          data-lpignore="true"
          data-1p-ignore="true"
          aria-invalid={showError}
          aria-describedby={showError ? errorId : undefined}
        />

        {showError && (
          <p id={errorId} className="text-base text-red-500">
            The last 4 digits you entered did not match.
          </p>
        )}
      </div>

      <Button
        variant="primary"
        className="m-auto mt-6"
        disabled={last4.length !== 4}
        onClick={onContinue}
      >
        Continue
      </Button>
    </FullPageCard>
  )
}

export function ClientMfaGateUnavailable({ organization }: { organization: PublicOrganization }) {
  return (
    <FullPageCard
      footer={
        <p className="mt-10 text-center text-sm text-gray-500">
          Powered by StanfordTax, Inc.
          <br /> 222 Broadway, New York, 10032
        </p>
      }
    >
      <div className="flex flex-col gap-1">
        {organization.logo && (
          <OrganizationLogo className="mx-auto" src={organization.logo} size={80} />
        )}
        <h3 className="text-center text-xl font-bold leading-9 tracking-tight text-gray-900">
          {organization.name ?? ' '}
        </h3>
      </div>
      <h1 className="mt-8 text-center text-2xl">Verification Unavailable</h1>
      <p className="my-4 text-center text-base text-gray-700">
        We could not verify your identity information for this organizer. Please contact your tax
        professional for access.
      </p>
    </FullPageCard>
  )
}

export function ClientMfaGateChecking({ organization }: { organization: PublicOrganization }) {
  return (
    <FullPageCard
      footer={
        <p className="mt-10 text-center text-sm text-gray-500">
          Powered by StanfordTax, Inc.
          <br /> 222 Broadway, New York, 10032
        </p>
      }
    >
      <div className="flex flex-col gap-1">
        {organization.logo && (
          <OrganizationLogo className="mx-auto" src={organization.logo} size={80} />
        )}
        <h3 className="text-center text-xl font-bold leading-9 tracking-tight text-gray-900">
          {organization.name ?? ' '}
        </h3>
      </div>
      <h1 className="mt-8 text-center text-2xl">Verification Required</h1>
      <p className="my-4 text-center text-base text-gray-700">Checking verification status...</p>
    </FullPageCard>
  )
}
